/**
 * PipelineTimelineV2 — Statistics 10B redesign.
 *
 * Layout: per-mockup `Image #23`.
 *   page header — search input + Latest button (handled in Statistics.jsx)
 *   photo header — filename + image_id + arrived_at + JourneyChain + total
 *   left pane    — 5 service summary rows (status, arrival, duration,
 *                  event count)
 *   right pane   — events grouped by service with sticky headers, diag
 *                  events get DurationBar, log events get a faint dot
 *   filter pills — All / Steps / Logs / Errors (client-side)
 *   filter input — text filter on event name + text
 *   empty state  — list of 5 most recent images instead of icon+sentence
 *
 * Data: /api/timeline/:image_id (PhotoArchive UNION live disk meta).
 * Recent-images fallback: /api/photos/?ordering=-created_at&limit=5.
 */

import { useEffect, useMemo, useState } from 'react';
import { fetchAllPhotos, fetchPhotoTimeline } from '../../../api';
import {
  DurationBar,
  JourneyChain,
  Mono,
  StatusPill,
  TabBar,
} from '../../../shared/components/redesign';
import { SERVICE_LABELS, SERVICE_DESCRIPTIONS, TIMELINE_SERVICE_ORDER } from '../constants';
import styles from './PipelineTimelineV2.module.css';

const FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'steps', label: 'Steps' },
  { id: 'logs', label: 'Logs' },
  { id: 'errors', label: 'Errors' },
];

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function formatMs(ms) {
  if (!Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function EmptyState({ recent, onPick }) {
  return (
    <div className={styles.emptyShell}>
      <Mono className={styles.emptyEyebrow}>Latest 5 photos</Mono>
      <ul className={styles.emptyList}>
        {recent.map((p) => (
          <li key={p.image_id}>
            <button
              type="button"
              className={styles.emptyRow}
              onClick={() => onPick(p)}
            >
              <Mono className={styles.emptyName}>{p.filename || p.image_id}</Mono>
              <span className={styles.emptySpacer} />
              {p.status && <StatusPill status={p.status} />}
            </button>
          </li>
        ))}
        {recent.length === 0 && (
          <li className={styles.emptyMessage}>No recent photos.</li>
        )}
      </ul>
    </div>
  );
}

export default function PipelineTimelineV2({ imageId, onSelectImage }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const [filter, setFilter] = useState('all');
  const [activeService, setActiveService] = useState(null);
  const [search, setSearch] = useState('');

  // Load timeline for the selected image.
  useEffect(() => {
    if (!imageId) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchPhotoTimeline(imageId)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        // Auto-highlight the first non-done service.
        const firstAttention = (d.services || []).find(
          (s) => s.status === 'attention' || s.status === 'error',
        );
        setActiveService(firstAttention?.key || null);
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn('PipelineTimelineV2: fetch failed', e);
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [imageId]);

  // Load 5 most recent photos for the empty state.
  useEffect(() => {
    if (imageId) return undefined;
    let cancelled = false;
    fetchAllPhotos(5, 0, { ordering: '-created_at' })
      .then((d) => {
        if (cancelled) return;
        const list = d?.results || d || [];
        setRecent(list.slice(0, 5));
      })
      .catch(() => {
        if (!cancelled) setRecent([]);
      });
    return () => {
      cancelled = true;
    };
  }, [imageId]);

  // Filtered events per service.
  const filteredByService = useMemo(() => {
    if (!data?.events_by_service) return {};
    const out = {};
    const lc = search.trim().toLowerCase();
    for (const [service, events] of Object.entries(data.events_by_service)) {
      out[service] = events.filter((e) => {
        if (filter === 'steps' && e.kind !== 'diag') return false;
        if (filter === 'logs' && e.kind !== 'log') return false;
        if (filter === 'errors' && e.status !== 'error' && e.kind !== 'error') {
          return false;
        }
        if (lc) {
          const haystack = `${e.name || ''} ${e.text || ''}`.toLowerCase();
          if (!haystack.includes(lc)) return false;
        }
        return true;
      });
    }
    return out;
  }, [data, filter, search]);

  if (!imageId) {
    return <EmptyState recent={recent} onPick={onSelectImage} />;
  }

  if (loading) {
    return <div className={styles.loading}>Loading timeline…</div>;
  }

  if (!data) {
    return (
      <div className={styles.errorShell}>
        <p>Could not load timeline for this photo.</p>
      </div>
    );
  }

  const image = data.image || {};
  const services = data.services || [];

  return (
    <div className={styles.container}>
      {/* Photo header — filename, id, journey, total */}
      <div className={styles.photoHeader}>
        <div className={styles.photoIdent}>
          <span className={styles.photoFile}>
            {image.filename}
            {image.is_archived && (
              <Mono className={styles.photoArchivedBadge}> · archived</Mono>
            )}
          </span>
          <Mono className={styles.photoMeta}>
            {image.id}
            {image.arrived_at && (
              <> · arrived {formatTime(image.arrived_at)}</>
            )}
          </Mono>
        </div>
        <span className={styles.photoSpacer} />
        <JourneyChain journey={image.journey || []} />
        {Number.isFinite(image.total_ms) && (
          <span className={styles.photoTotal}>
            <Mono className={styles.photoTotalEyebrow}>Total</Mono>
            <span className={styles.photoTotalValue}>{formatMs(image.total_ms)}</span>
          </span>
        )}
      </div>

      <div className={styles.body}>
        {/* Left pane — service summary list */}
        <aside className={styles.left}>
          <Mono className={styles.leftEyebrow}>
            {services.length} service{services.length === 1 ? '' : 's'}
          </Mono>
          {TIMELINE_SERVICE_ORDER.map((key, idx) => {
            const s = services.find((x) => x.key === key);
            if (!s) return null;
            const isActive = key === activeService;
            return (
              <button
                key={key}
                type="button"
                className={`${styles.svcRow} ${isActive ? styles.svcActive : ''}`}
                onClick={() => setActiveService(isActive ? null : key)}
              >
                <div className={styles.svcHead}>
                  <Mono className={styles.svcRank}>
                    {String(idx + 1).padStart(2, '0')}
                  </Mono>
                  <span className={styles.svcName}>
                    {SERVICE_LABELS[key] || key}
                  </span>
                  <span className={styles.svcSpacer} />
                  <StatusPill status={s.status} />
                </div>
                <Mono className={styles.svcDesc}>
                  {SERVICE_DESCRIPTIONS[key] || ''}
                </Mono>
                <div className={styles.svcStats}>
                  <Mono className={styles.svcStat}>
                    <span className={styles.svcStatLabel}>dur</span>
                    {formatMs(s.duration_ms || 0)}
                  </Mono>
                  <Mono className={styles.svcStat}>
                    <span className={styles.svcStatLabel}>events</span>
                    {s.event_count ?? 0}
                  </Mono>
                </div>
              </button>
            );
          })}
        </aside>

        {/* Right pane — events grouped by service */}
        <section className={styles.right}>
          <div className={styles.eventToolbar}>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter events…"
              className={styles.eventSearch}
            />
            <TabBar
              active={filter}
              onChange={setFilter}
              tabs={FILTER_TABS}
            />
          </div>

          {TIMELINE_SERVICE_ORDER.map((key) => {
            const s = services.find((x) => x.key === key);
            if (!s) return null;
            const events = filteredByService[key] || [];
            if (events.length === 0 && filter !== 'all') return null;
            // Compute max duration in this group for proportional bars.
            const maxMs = Math.max(
              0,
              ...events
                .filter((e) => e.kind === 'diag')
                .map((e) => e.duration_ms || 0),
            );
            return (
              <div key={key} className={styles.svcGroup}>
                <header className={styles.svcGroupHead}>
                  <Mono className={styles.svcGroupName}>
                    {SERVICE_LABELS[key] || key}
                  </Mono>
                  <Mono className={styles.svcGroupDesc}>
                    {SERVICE_DESCRIPTIONS[key] || ''}
                  </Mono>
                  <span className={styles.svcSpacer} />
                  <StatusPill status={s.status} />
                  <Mono className={styles.svcGroupTotal}>
                    {formatMs(s.duration_ms || 0)}
                  </Mono>
                </header>
                <ul className={styles.eventList}>
                  {events.length === 0 && (
                    <li className={styles.eventEmpty}>No matching events.</li>
                  )}
                  {events.map((e, i) => (
                    <li key={`${e.ts}-${i}`} className={styles.eventRow}>
                      <Mono className={styles.eventTs}>{formatTime(e.ts)}</Mono>
                      {e.kind === 'diag' ? (
                        <>
                          <Mono className={styles.eventName}>{e.name}</Mono>
                          <span className={styles.eventBar}>
                            <DurationBar
                              ms={e.duration_ms}
                              maxMs={maxMs}
                              status={e.status}
                              width={280}
                            />
                          </span>
                          <Mono className={styles.eventDur}>
                            {formatMs(e.duration_ms)}
                          </Mono>
                        </>
                      ) : (
                        <>
                          <span className={styles.eventLogDot} aria-hidden="true" />
                          <span className={styles.eventLogText}>{e.text}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </section>
      </div>
    </div>
  );
}
